/***************************************************************************

    Copyright (C) 2020 Ravenpod, Inc.
    All rights reserved.

    This material contains CONFIDENTIAL INFORMATION
    that is the property of Ravenpod.com, Inc.
    Any unauthorized use, duplication or disclosure is prohibited.

***************************************************************************/
import MomentTZ from 'moment-timezone'
import humanizeDuration  from "humanize-duration";

const shortEnglishHumanizer = humanizeDuration.humanizer({
    language: "shortEn",
    languages: {
      shortEn: {
        y: () => "years",
        mo: () => "months",
        w: () => "weeks",
        d: () => "days",
        h: () => "hrs",
        m: () => "mins",
        s: () => "sec",
        ms: () => "ms",
      },
    },
});

const funcs = {

    relativeTime: (timestamp) => {
        const timeInUTC = MomentTZ(timestamp);
        const timeInUserTimeZone = timeInUTC.tz(funcs.getUserTimezone())        
        return timeInUserTimeZone.fromNow();  
    },

    getFriendlyDurationTime: (timeInMs, units) => {
        return shortEnglishHumanizer(timeInMs, { largest: 1, units: units, round: true } );                
    },

    getFriendlyDuration: (startTime, endTime, units) => { // units in the array of ['y', 'mo', 'w', 'd', 'h', 'm', 's', 'ms']
        if (!endTime) {
            endTime = (new Date()).toJSON();
        }
        if (units) {
            return shortEnglishHumanizer(new Date(endTime) - new Date(startTime), { largest: 1, units: units, round: true } );        
        } else {
            return shortEnglishHumanizer(new Date(endTime) - new Date(startTime), { largest: 1, round: true } );        
        }
    },

    getFriendlyDurationSplitUnit: (startTime, endTime, units) => { // Will return [12, 'hrs']
        if (!endTime) {
            endTime = (new Date()).toJSON();
        }
        let fullDesc
        if (units) {
            fullDesc = shortEnglishHumanizer(new Date(endTime) - new Date(startTime), { largest: 1, units: units, round: true } );        
        } else {
            fullDesc = shortEnglishHumanizer(new Date(endTime) - new Date(startTime), { largest: 1, round: true } );        
        }
        let result = fullDesc.split(' ')
        return result;
    },

    getFriendlyTimeElapsedFromNow: (startTime) => { // Will return [12, 'hrs']
        let fullDesc = shortEnglishHumanizer(new Date() - new Date(startTime), { largest: 1 } );
        let result = fullDesc.split(' ')
        return result;
    },
    
    getUserTimezone: () => {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
    },

    startAndEndOflastXMinInEpoch: (min) => {
        const currentTime = MomentTZ();

        // const goodTime = MomentTZ.tz("2021-01-14 18:25:00.000", "UTC");
        // const offset = Math.round( (currentTime.clone().tz('utc').valueOf() - goodTime.valueOf()) / (1000 * 60) );
        const offset = 0

        const endTimeWithTimeZone = currentTime.clone().tz('utc').subtract(offset, 'minute')
        const startTimeWithTimeZone = currentTime.clone().tz('utc').subtract(min + offset, 'minute')

        // const someTimeAgo = currentTime.clone().tz('utc').subtract(min + Constants.OFFSET_FROM_JAN_14_930AM_IN_MINUTES, 'minute')

        return [startTimeWithTimeZone.unix(), endTimeWithTimeZone.unix()]
    },

    toDateFormatOnGraph: (timestamp) => {
        const timeInUTC = MomentTZ(timestamp);
        const timeInUserTimeZone = timeInUTC.tz(funcs.getUserTimezone())
        return timeInUserTimeZone.format("k:mm a")
    },

    toHHMMSSFormat: (timestamp) => {
        const timeInUTC = MomentTZ(timestamp);
        const timeInUserTimeZone = timeInUTC.tz(funcs.getUserTimezone())
        return timeInUserTimeZone.format("HH:mm:ss a")
    },

    toShortFormat: (timestamp) => {
        const timeInUTC = MomentTZ(timestamp);
        const timeInUserTimeZone = timeInUTC.tz(funcs.getUserTimezone())
        return timeInUserTimeZone.format("HH:mm:ss.SSS")
    },

    toShortFormatWithAmPm: (timestamp) => {
        const timeInUTC = MomentTZ(timestamp);
        const timeInUserTimeZone = timeInUTC.tz(funcs.getUserTimezone())
        return timeInUserTimeZone.format("HH:mm:ss.SSS a")
    },

    toLongFormat: (timestamp) => {
        const timeInUTC = MomentTZ(timestamp);
        const timeInUserTimeZone = timeInUTC.tz(funcs.getUserTimezone())
        return timeInUserTimeZone.format("MMM DD, HH:mm:ss.SSS a")
    },

    toLongFormatWithoutMin: (timestamp) => {
        const timeInUTC = MomentTZ(timestamp);
        const timeInUserTimeZone = timeInUTC.tz(funcs.getUserTimezone())
        return timeInUserTimeZone.format("MMM DD, HH:mm a")
    },

    toLongFormatWithoutMS: (timestamp) => {
        const timeInUTC = MomentTZ(timestamp);
        const timeInUserTimeZone = timeInUTC.tz(funcs.getUserTimezone())
        return timeInUserTimeZone.format("MMM DD, HH:mm:ss a")
    },

    toLongFormatWithYear: (timestamp) => {
        const timeInUTC = MomentTZ(timestamp);
        const timeInUserTimeZone = timeInUTC.tz(funcs.getUserTimezone())
        return timeInUserTimeZone.format("YYYY MMM DD, HH:mm:ss A")
    },

    toLongFormatWithYearAndMilliSec: (timestamp) => {
        const timeInUTC = MomentTZ(timestamp);
        const timeInUserTimeZone = timeInUTC.tz(funcs.getUserTimezone())
        return timeInUserTimeZone.format("YYYY MMM DD, HH:mm:ss.SSS A")
    },

    toEpochTimeFromJSONDate: (jsonDate) => {
        return new Date(jsonDate).getTime();
    },

    numOfHoursSince: (timestamp) => {
        return Math.floor( (new Date() - new Date(timestamp)) / (1000 * 60 * 60) )
    }

}

export default funcs